import CardWrapper from '@components/UI/CardWrapper';
import { Popper } from '@material-ui/core';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ItemSelector } from '@services/requests/companyProfileService/interface';
import { MainData } from '@services/requests/companyProfileService/types/mainData';
import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import {
  CompanyProfileExtendedFormFields,
  CompanyProfileFormFields,
} from '../../CompanyProfileModulesWrapper/types';
import { CompanyModuleStyledContainer } from './StyledComponents';

interface CompanyModuleProps {
  isEditMode: boolean;
  form: UseFormReturn<
    CompanyProfileFormFields & CompanyProfileExtendedFormFields,
    any,
    CompanyProfileFormFields & CompanyProfileExtendedFormFields
  >;
  mainData: MainData;
}

const CustomPopper = (props: any) => {
  return (
    <Popper
      {...props}
      placement="bottom"
      style={{ width: props.anchorEl.clientWidth, minWidth: 'fit-content' }}
    />
  );
};

function CompanyModule({ isEditMode, form, mainData }: CompanyModuleProps) {
  const { fields } = mainData.sections.common;

  const {
    control,
    formState: { errors },
  } = form;

  return (
    <CompanyModuleStyledContainer>
      <CardWrapper sx={{ padding: '25px 40px' }} className="card">
        <Typography variant={'h3'} className={'module-title'}>
          {mainData.sections.common.caption}:
        </Typography>
        <Controller
          control={control}
          name="fullName"
          render={({ field: { onChange, value, ref, onBlur, name } }) => (
            <TextField
              fullWidth
              type="text"
              className="comment-field"
              size={'medium'}
              disabled={!isEditMode}
              label={fields.full_name.title}
              variant="outlined"
              multiline
              minRows={2}
              name={name}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              value={value || ''}
              error={!!errors.fullName}
              InputProps={{ classes: { inputMultiline: 'browser-default' } }}
            />
          )}
        />
        <Controller
          control={control}
          name="shortName"
          render={({ field: { onChange, value, ref, onBlur, name } }) => (
            <TextField
              fullWidth
              type="text"
              className="comment-field"
              size={'medium'}
              disabled={!isEditMode}
              label={fields.short_name.title}
              variant="outlined"
              name={name}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              value={value || ''}
              error={!!errors.shortName}
              InputProps={{ classes: { input: 'browser-default' } }}
            />
          )}
        />
        {mainData.sections.common.company_type === '2' && (
          <>
            <Controller
              control={control}
              name="firstName"
              render={({ field: { onChange, value, ref, onBlur, name } }) => (
                <TextField
                  fullWidth
                  type="text"
                  className="comment-field"
                  size={'medium'}
                  disabled={!isEditMode}
                  label={fields.first_name.title}
                  variant="outlined"
                  name={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  value={value || ''}
                  error={!!errors.firstName}
                  InputProps={{ classes: { input: 'browser-default' } }}
                />
              )}
            />
            <Controller
              control={control}
              name="lastName"
              render={({ field: { onChange, value, ref, onBlur, name } }) => (
                <TextField
                  fullWidth
                  type="text"
                  className="comment-field"
                  size={'medium'}
                  disabled={!isEditMode}
                  label={fields.last_name.title}
                  variant="outlined"
                  name={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  value={value || ''}
                  error={!!errors.lastName}
                  InputProps={{ classes: { input: 'browser-default' } }}
                />
              )}
            />
            <Controller
              control={control}
              name="secondName"
              render={({ field: { onChange, value, ref, onBlur, name } }) => (
                <TextField
                  fullWidth
                  type="text"
                  className="comment-field"
                  size={'medium'}
                  disabled={!isEditMode}
                  label={fields.second_name.title}
                  variant="outlined"
                  name={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  value={value || ''}
                  error={!!errors.secondName}
                  InputProps={{ classes: { input: 'browser-default' } }}
                />
              )}
            />
          </>
        )}
        <div className="digital-codes-wrapper">
          <TextField
            fullWidth
            type="text"
            className="comment-field"
            size={'medium'}
            disabled={true}
            label={fields.inn.title}
            variant="outlined"
            value={fields.inn.value}
            InputProps={{ classes: { input: 'browser-default' } }}
          />
          <Controller
            control={control}
            name="kpp"
            render={({ field: { onChange, value, ref, onBlur, name } }) => (
              <TextField
                fullWidth
                type="text"
                className="comment-field"
                size={'medium'}
                disabled={!isEditMode}
                label={fields.kpp.title}
                variant="outlined"
                name={name}
                onChange={onChange}
                onBlur={onBlur}
                ref={ref}
                value={value || ''}
                error={!!errors.kpp}
                InputProps={{ classes: { input: 'browser-default' } }}
              />
            )}
          />
          <TextField
            fullWidth
            type="text"
            className="comment-field"
            size={'medium'}
            disabled={true}
            label={fields.ogrn.title}
            variant="outlined"
            value={fields.inn.value}
            InputProps={{ classes: { input: 'browser-default' } }}
          />
        </div>
        <Controller
          control={control}
          name="legalAddress"
          render={({ field: { onChange, value, ref, onBlur, name } }) => (
            <TextField
              fullWidth
              type="text"
              multiline
              minRows={2}
              className="comment-field"
              size={'medium'}
              disabled={!isEditMode}
              label={fields.legal_address.title}
              variant="outlined"
              name={name}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              value={value || ''}
              error={!!errors.legalAddress}
              InputProps={{ classes: { inputMultiline: 'browser-default' } }}
            />
          )}
        />
        <Controller
          control={control}
          name="postalAddress"
          render={({ field: { onChange, value, ref, onBlur, name } }) => (
            <TextField
              fullWidth
              type="text"
              multiline
              minRows={2}
              className="comment-field"
              size={'medium'}
              disabled={!isEditMode}
              label={fields.postal_address.title}
              variant="outlined"
              name={name}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              value={value || ''}
              error={!!errors.postalAddress}
              InputProps={{ classes: { inputMultiline: 'browser-default' } }}
            />
          )}
        />
        <div className="other-info-wrapper">
          <TextField
            fullWidth
            type="text"
            className="comment-field"
            size={'medium'}
            disabled={true}
            label={fields.country.title}
            variant="outlined"
            value={fields.country.value}
            InputProps={{ classes: { input: 'browser-default' } }}
          />

          {/* <Controller
            control={control}
            name="settlementsCurrency"
            render={({ field: { onChange, value, ref, onBlur } }) => (
              <Autocomplete
                disablePortal
                fullWidth
                options={currencies}
                disabled={!isEditMode}
                onChange={(_, value) => onChange(value ? +value.id : null)}
                onBlur={onBlur}
                ref={ref}
                value={value ? currencies.find((c) => c.id === value.toString()) ?? null : null}
                popupIcon={<KeyboardArrowDownIcon />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('CompanyProfile:BankDetails.Currency')}
                    error={!!errors.settlementsCurrency}
                  />
                )}
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      {...props}
                      value={option.id}
                      key={option.id}
                      className="a-service-field-selector-option--root"
                    >
                      {option.code}
                    </MenuItem>
                  );
                }}
                getOptionLabel={(option: CurrencyData) => option.code}
              />
            )}
          /> */}

          <Controller
            control={control}
            name="settlementsCurrency"
            render={({ field: { onChange, value, ref, onBlur } }) => (
              <Autocomplete
                disablePortal
                fullWidth
                className="settlements-currency"
                PopperComponent={CustomPopper}
                options={fields.currency.items ?? []}
                disabled={!isEditMode}
                onChange={(_, value) => onChange(value ? value.code : null)}
                onBlur={onBlur}
                ref={ref}
                value={
                  value ? (fields.currency.items ?? []).find((c) => c.code === value) ?? null : null
                }
                popupIcon={<KeyboardArrowDownIcon />}
                classes={{ input: 'browser-default' }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={fields.currency.title}
                    error={!!errors.settlementsCurrency}
                  />
                )}
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      {...props}
                      value={option.code}
                      key={option.code}
                      className="a-service-field-selector-option--root"
                    >
                      {option.title}
                    </MenuItem>
                  );
                }}
                getOptionLabel={(option: ItemSelector) => option.title}
              />
            )}
          />
        </div>
      </CardWrapper>
    </CompanyModuleStyledContainer>
  );
}
export { CompanyModule };
